/**
 * RoomReadinessBoard — read-only visual surface of every room in the
 * organization, grouped by current_status in operational-urgency order.
 *
 * Filters (client-side; the data set fits comfortably in memory) adapt
 * to the tenant's `tenant_settings.property_type` (PROJECT_STATE #64):
 *
 *   dorm           — campus → building → floor pickers (full hierarchy)
 *   office         — building → floor pickers          (no campus level)
 *   property_mgmt  — building only                     (each "building" = one home)
 *
 * No transitions originate from this page. Status changes still go
 * through the room-transition Edge Function via TaskDetailPage or
 * SupervisorDashboard. The board is a visibility tool — the audit-flagged
 * gap was that operations had no way to see the org's room state at a
 * glance, not that they needed another mutation surface.
 */

import { useMemo, useState, type ReactNode } from 'react'
import { Link }                               from 'react-router'
import { useTranslation }                     from 'react-i18next'
import {
  useRoomBoard,
  STATUS_DISPLAY_ORDER,
  type RoomBoardRow,
  type RoomStatus,
} from '@/hooks/useRoomBoard'
import { useTenantPropertyType }              from '@/hooks/useTenantPropertyType'
import { roomDetailPath }                     from '@/router/paths'

export function RoomReadinessBoard(): ReactNode {
  const { t } = useTranslation()
  const { data, isLoading, error } = useRoomBoard()
  const { propertyType } = useTenantPropertyType()
  // Memo the empty-array fallback so downstream useMemo deps don't see a
  // fresh identity on every render (eslint react-hooks/exhaustive-deps).
  const rooms = useMemo<RoomBoardRow[]>(() => data ?? [], [data])

  const showCampus   = propertyType === 'dorm'
  const showFloor    = propertyType !== 'property_mgmt'

  const [campusFilter,   setCampusFilter]   = useState<string>('all')
  const [buildingFilter, setBuildingFilter] = useState<string>('all')
  const [floorFilter,    setFloorFilter]    = useState<string>('all')

  const campuses  = useMemo(() => uniqueOptions(rooms, (r) => r.campus), [rooms])
  const buildings = useMemo(() => {
    const scoped = campusFilter === 'all'
      ? rooms
      : rooms.filter((r) => r.campus?.id === campusFilter)
    return uniqueOptions(scoped, (r) => r.building)
  }, [rooms, campusFilter])
  const floors    = useMemo(() => {
    let scoped = rooms
    if (campusFilter   !== 'all') scoped = scoped.filter((r) => r.campus?.id   === campusFilter)
    if (buildingFilter !== 'all') scoped = scoped.filter((r) => r.building?.id === buildingFilter)
    return uniqueOptions(scoped, (r) => r.floor ? { id: r.floor.id, name: r.floor.label } : null)
  }, [rooms, campusFilter, buildingFilter])

  const filtered = useMemo(() => rooms.filter((r) => {
    if (showCampus && campusFilter !== 'all' && r.campus?.id   !== campusFilter)   return false
    if (buildingFilter             !== 'all' && r.building?.id !== buildingFilter) return false
    if (showFloor && floorFilter   !== 'all' && r.floor?.id    !== floorFilter)    return false
    return true
  }), [rooms, showCampus, showFloor, campusFilter, buildingFilter, floorFilter])

  const grouped = useMemo(() => groupByStatus(filtered), [filtered])

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('board.title', 'Room readiness board')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
          {t('board.subtitle', 'Live view of every room, grouped by status (operational urgency).')}
        </p>
      </header>

      <FilterBar
        showCampus={showCampus}
        showFloor={showFloor}
        campuses={campuses}
        buildings={buildings}
        floors={floors}
        campus={campusFilter}
        building={buildingFilter}
        floor={floorFilter}
        onCampusChange={(v) => {
          setCampusFilter(v); setBuildingFilter('all'); setFloorFilter('all')
        }}
        onBuildingChange={(v) => { setBuildingFilter(v); setFloorFilter('all') }}
        onFloorChange={setFloorFilter}
      />

      {isLoading && <Hint>{t('board.loading', 'Loading rooms…')}</Hint>}
      {error    && (
        <ErrorPanel
          message={
            error instanceof Error
              ? error.message
              : t('board.failedToLoad', 'Failed to load the room board.')
          }
        />
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <Hint>{t('board.empty', 'No rooms match the current filters.')}</Hint>
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {STATUS_DISPLAY_ORDER.map((status) => {
            const list = grouped.get(status) ?? []
            if (list.length === 0) return null
            return <StatusGroup key={status} status={status} rooms={list} />
          })}
        </div>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────

interface FilterOption { id: string; name: string }

function FilterBar(props: {
  showCampus: boolean
  showFloor:  boolean
  campuses:   FilterOption[]
  buildings:  FilterOption[]
  floors:     FilterOption[]
  campus:     string
  building:   string
  floor:      string
  onCampusChange:   (v: string) => void
  onBuildingChange: (v: string) => void
  onFloorChange:    (v: string) => void
}): ReactNode {
  const { t } = useTranslation()
  return (
    <div
      style={{
        display:        'flex',
        gap:            '0.75rem',
        flexWrap:       'wrap',
        background:     'var(--brand-white)',
        border:         '1px solid var(--brand-neutral-300)',
        borderRadius:   'var(--brand-radius-md)',
        padding:        '0.5rem 0.75rem',
      }}
    >
      {props.showCampus && (
        <Select
          label={t('board.filterCampus', 'Campus')}
          value={props.campus}
          onChange={props.onCampusChange}
          options={[{ id: 'all', name: t('board.allCampuses', 'All campuses') }, ...props.campuses]}
        />
      )}
      <Select
        label={t('board.filterBuilding', 'Building')}
        value={props.building}
        onChange={props.onBuildingChange}
        options={[{ id: 'all', name: t('board.allBuildings', 'All buildings') }, ...props.buildings]}
      />
      {props.showFloor && (
        <Select
          label={t('board.filterFloor', 'Floor')}
          value={props.floor}
          onChange={props.onFloorChange}
          options={[{ id: 'all', name: t('board.allFloors', 'All floors') }, ...props.floors]}
        />
      )}
    </div>
  )
}

function Select(props: {
  label:    string
  value:    string
  onChange: (v: string) => void
  options:  FilterOption[]
}): ReactNode {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
      {props.label}
      <select
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        style={{
          marginTop:    '0.2rem',
          padding:      '0.4rem 0.5rem',
          border:       '1px solid var(--brand-neutral-300)',
          borderRadius: 'var(--brand-radius-sm)',
          fontFamily:   'inherit',
          fontSize:     '0.9rem',
          background:   'var(--brand-white)',
          color:        'var(--brand-neutral-900)',
        }}
      >
        {props.options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </label>
  )
}

function StatusGroup({ status, rooms }: { status: RoomStatus; rooms: RoomBoardRow[] }): ReactNode {
  const { t } = useTranslation()
  return (
    <section
      data-status={status}
      style={{
        background:   'var(--brand-white)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.75rem 1rem',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <strong style={{ textTransform: 'capitalize' }}>
          {t(`status.${status}`, status.replace(/_/g, ' '))}
        </strong>
        <span
          style={{
            fontSize:     '0.75rem',
            padding:      '0.1rem 0.55rem',
            borderRadius: 'var(--brand-radius-full)',
            background:   statusBadgeBg(status),
            color:        statusBadgeFg(status),
          }}
        >
          {rooms.length}
        </span>
      </header>
      <ul style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: '0.4rem',
      }}>
        {rooms.map((r) => (
          <li key={r.id} style={{ listStyle: 'none' }}>
            <Link
              to={roomDetailPath(r.id)}
              data-testid={`room-${r.id}`}
              style={{
                padding:        '0.4rem 0.6rem',
                border:         '1px solid var(--brand-neutral-300)',
                borderRadius:   'var(--brand-radius-sm)',
                fontSize:       '0.85rem',
                display:        'flex',
                flexDirection:  'column',
                color:          'inherit',
                textDecoration: 'none',
                background:     'var(--brand-white)',
              }}
            >
              <strong>{r.room_number}</strong>
              <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.75rem' }}>
                {r.building?.name ?? '—'} · {r.floor?.label ?? '—'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────

function uniqueOptions(
  rooms: RoomBoardRow[],
  pick:  (r: RoomBoardRow) => { id: string; name: string } | null,
): FilterOption[] {
  const seen = new Map<string, string>()
  for (const r of rooms) {
    const item = pick(r)
    if (item && !seen.has(item.id)) seen.set(item.id, item.name)
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

function groupByStatus(rooms: RoomBoardRow[]): Map<RoomStatus, RoomBoardRow[]> {
  const out = new Map<RoomStatus, RoomBoardRow[]>()
  for (const r of rooms) {
    const list = out.get(r.current_status) ?? []
    list.push(r)
    out.set(r.current_status, list)
  }
  return out
}

function statusBadgeBg(s: RoomStatus): string {
  switch (s) {
    case 'maintenance_required':
    case 'inspection_pending':   return 'var(--brand-danger-subtle)'
    case 'ready':
    case 'occupied':             return 'var(--brand-success-subtle)'
    default:                     return 'var(--brand-primary-subtle)'
  }
}

function statusBadgeFg(s: RoomStatus): string {
  switch (s) {
    case 'maintenance_required':
    case 'inspection_pending':   return 'var(--brand-danger)'
    case 'ready':
    case 'occupied':             return 'var(--brand-success)'
    default:                     return 'var(--brand-primary)'
  }
}

function Hint({ children }: { children: ReactNode }): ReactNode {
  return <p style={{ color: 'var(--brand-neutral-500)' }}>{children}</p>
}

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.75rem',
        fontSize:     '0.85rem',
      }}
    >
      {message}
    </div>
  )
}
